import type {
  BigQueryTelemetryMode,
  BigQueryUsageMonitoring,
} from "@/features/bigquery-usage/api/bigquery-usage.api";

/**
 * R19's three zero states, plus the one an admin causes themselves.
 *
 * `not-recording` is a property of the deployment; `no-usage-in-window` is the
 * ordinary quiet window a healthy production environment hits most often; and
 * `recording-broken` is the one case where a zero is a fault rather than a fact.
 * Those three are R19's, and they describe the environment.
 *
 * `no-match-for-narrowing` is a different kind of statement: the window may be
 * busy and recording fine, and the empty screen is the admin's own filter or
 * search term. It earns a member of its own because the alternative is telling
 * someone mid-search that their window "was simply quiet", which is both wrong
 * and a dead end — it names no way back out.
 *
 * Cloud Monitoring being unreadable is deliberately **not** a member. That is
 * the reconciliation panel's own degrade state under R20, and it says nothing
 * about which of these a window is in.
 */
export type BigQueryZeroState =
  | "not-recording"
  | "no-usage-in-window"
  | "recording-broken"
  | "no-match-for-narrowing";

export interface BigQueryZeroStateInput {
  /** From `/stats`, per KTD21. Undefined only until that read resolves. */
  telemetryMode: BigQueryTelemetryMode | undefined;
  recordedQueries: number;
  monitoring: BigQueryUsageMonitoring | undefined;
  /**
   * Whether a narrowing is active. Cloud Monitoring accounts for the whole GCP
   * project and takes no filter, so it can only contradict a window-wide zero —
   * a feature filter that matches nothing is not broken recording.
   */
  isNarrowed?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Which zero state this window is in, or null when it has usage to show.
 *
 * The order of the branches is the whole contract. The mode comes first because
 * an environment that records nothing produces the same zero an idle window
 * does, and only the API can tell them apart (KTD21) — a row count never can,
 * in either direction.
 */
export function resolveBigQueryZeroState({
  telemetryMode,
  recordedQueries,
  monitoring,
  isNarrowed = false,
}: BigQueryZeroStateInput): BigQueryZeroState | null {
  if (telemetryMode === undefined) return null;
  if (telemetryMode === "not-recording") return "not-recording";
  if (recordedQueries > 0) return null;

  // Null is Monitoring not having answered, and it falls through to the quiet
  // window on purpose: an unreadable second opinion is not evidence of a fault,
  // and turning a healthy idle hour into "recording appears broken" would have
  // the tab lie to an operator about their own system.
  const reportedQueries = monitoringQueryCount(monitoring);
  if (!isNarrowed && reportedQueries != null && reportedQueries > 0) return "recording-broken";

  // Last, so the deployment-level explanations win: in an environment that
  // records nothing, a search matching nothing is explained by the environment
  // rather than by the term.
  if (isNarrowed) return "no-match-for-narrowing";

  return "no-usage-in-window";
}

/**
 * Google's query count for the window, or null when it did not answer.
 *
 * The route sends a structural zero for a metric it could not read, so the flags
 * are what separate "Google saw nothing" from "Google did not say".
 */
export function monitoringQueryCount(
  monitoring: BigQueryUsageMonitoring | undefined,
): number | null {
  if (!monitoring?.available || !monitoring.queriesAvailable) return null;
  return Math.round(monitoring.totalQueries);
}

export interface BigQueryRetentionNote {
  retentionDays: number;
  /** ISO instant rows are deleted before, for the copy to name. */
  cutoffAt: string;
}

export interface BigQueryRetentionInput {
  /** The window's resolved start. Undefined until the totals read resolves. */
  windowStart: string | undefined;
  retentionDays: number;
  now: Date;
}

/**
 * R24. The cutoff a window reaches past, or null when it stays inside retention.
 *
 * A 30-day window under 7-day retention is mostly empty by design, and without
 * this note those empty buckets read as a collapse in traffic rather than as
 * housekeeping that already happened.
 */
export function resolveRetentionNote({
  windowStart,
  retentionDays,
  now,
}: BigQueryRetentionInput): BigQueryRetentionNote | null {
  if (windowStart === undefined || retentionDays <= 0) return null;

  const startedAt = Date.parse(windowStart);
  if (Number.isNaN(startedAt)) return null;

  const cutoff = now.getTime() - retentionDays * DAY_MS;
  if (startedAt >= cutoff) return null;

  return { retentionDays, cutoffAt: new Date(cutoff).toISOString() };
}
